import type { SearchProductCard } from '../inventory/types';

/**
 * 【用户设计 2026-08-17】布局找货：搜索产品 → 按批次列出所在位置，
 * 按临期程度从红到绿排序/着色；点击跳转到对应场景并高亮位置矩形。
 * 本文件是纯函数模型（扁平化/排序/配色），组件与画布只消费结果。
 */

export interface LayoutSearchHit {
  /** batchId——列表 key 与去重键 */
  readonly key: string;
  readonly productName: string;
  readonly batchNo: string;
  readonly expirationDate: string | null;
  readonly hasExpiration: boolean;
  readonly onHandBase: string;
  readonly units: SearchProductCard['units'];
  readonly locations: SearchProductCard['batches'][number]['locations'];
}

/** 距到期天数；无效期 = Infinity（排最后、灰色） */
export function daysUntilExpiry(
  expirationDate: string | null,
  hasExpiration: boolean,
  now = Date.now(),
): number {
  if (!hasExpiration || !expirationDate) return Number.POSITIVE_INFINITY;
  return Math.floor((Date.parse(`${expirationDate}T00:00:00Z`) - now) / 86_400_000);
}

/** 临期程度 → 颜色（红→橙→黄→黄绿→绿；无效期灰）。深浅主题下都可读。 */
export function expiryColor(
  expirationDate: string | null,
  hasExpiration: boolean,
  now = Date.now(),
): string {
  const days = daysUntilExpiry(expirationDate, hasExpiration, now);
  if (!Number.isFinite(days)) return '#8b8d98';
  if (days < 0) return '#e5484d';
  if (days <= 7) return '#f76b15';
  if (days <= 30) return '#ffb224';
  if (days <= 90) return '#94ba2c';
  return '#46a758';
}

/** 搜索响应 → 批次行（只保留有货批次），按临期升序（最紧急在最上） */
export function flattenHits(
  cards: readonly SearchProductCard[],
  now = Date.now(),
): LayoutSearchHit[] {
  const hits: LayoutSearchHit[] = [];
  for (const card of cards) {
    for (const batch of card.batches) {
      if (batch.locations.length === 0) continue;
      if (BigInt(batch.onHandBase) <= 0n) continue;
      hits.push({
        key: batch.batchId,
        productName: card.name,
        batchNo: batch.batchNo,
        expirationDate: batch.expirationDate,
        hasExpiration: batch.hasExpiration,
        onHandBase: batch.onHandBase,
        units: card.units,
        locations: batch.locations,
      });
    }
  }
  return hits.sort(
    (a, b) =>
      daysUntilExpiry(a.expirationDate, a.hasExpiration, now) -
      daysUntilExpiry(b.expirationDate, b.hasExpiration, now),
  );
}

// 注：曾有"按全局最紧急批次给画布所有命中位置染色"的 hitColorsByLocation——
// 用户裁决（2026-08-17）：临期红绿只属于左侧清单；画布高亮只跟随点击的那一行
// （黄色边框），因此该函数删除。
